// @flow
import * as React from 'react';
import { Button, Divider, Stack } from '@mui/material';
import { FunnelSimple } from '@phosphor-icons/react';
import _ from 'lodash';
import {
  clientColumns,
  TableSettings,
  useTableSettings,
  withTableSettings
} from '../../features';
import { useClientListFilterDrawer, withClientListFilterDrawer } from './ui/client-list-filter-drawer';
import { CreateProfileButton } from '../client/ui/client-personal/contact-info-form';
import {
  ClientFilter, ClientListQuery,
  DataGrid,
  FilterBadges,
  ListPageWrapper,
  PageTitle, useAgent,
  useClients, useSessionStorageState
} from '../../shared';
import {
  ClientViewFilterInput,
  ClientViewSortInput,
  Field,
  PageInput,
  SortDirection
} from '@coverright/data-access/types/enrollment';
import { SearchInput } from '@coverright/ui/inputs';
import { useClientsToCall } from './hooks/use-clients-to-call';
import { enrollmentApolloClient } from '@coverright/data-access/apollo-clients';
import { gql } from '@apollo/client';

const Page = () => {
  const [filters, setFilters] = useFilters();
  const {columns} = useTableSettings(clientColumns, filters, settings => setFilters(settings.filters));
  const [page, setPage] = usePage();
  const [sort, setSort] = useSort();
  const {clients, loading, total} = useClients(page, filters, sort);
  const {open} = useClientListFilterDrawer();
  const agent = useAgent();

  const clientsToCall = useClientsToCall();

  const openFilters = () => open(setFilters, filters)

  const onFilterRemoveClick = (filterName: keyof ClientViewFilterInput) => {
    setFilters(prev => {
      setPage(p => ({...p, page: 0}));
      return _.omit(prev, filterName)
    })
  }

  return (
    <ListPageWrapper>
      <PageTitle title={'Client list'}>
        <TableSettings />
        <Stack direction={'row'} gap={2}>
          <SearchInput value={filters?.searchTerm || ''}
                       loading={loading}
                       onChange={searchTerm => setFilters(p => ({...p, searchTerm}))} />
          <Button variant={'outlined'}
                  data-test={'clients-filter-button'}
                  onClick={openFilters}
                  startIcon={<FunnelSimple weight={'bold'} />}
                  size={'small'} sx={{minWidth: 90, alignSelf: 'center'}}>
            Filters
          </Button>
          <Divider orientation={'vertical'} />
          <CreateProfileButton />
        </Stack>
      </PageTitle>

      <FilterBadges onRemoveFilterClick={onFilterRemoveClick} filter={filters} nameMapFunc={filterNameFunc} />

      <DataGrid columns={columns}
                page={page.page}
                rowsPerPage={page.size}
                total={total}
                getRowClassName={row => (
                  clientsToCall.some(c => c.clientId === row.clientId) ?
                    clientsToCall.find(c => c.clientId === row.clientId)?.currentAssignment?.assignedAgentId === agent?.id ? 'assigned-client-data-grid-row' : 'assigned-client-to-other-agent-data-grid-row'
                    : ''
                )}
                onRowMouseEnter={(e, row) => prefillClientOutputInCache(row)}
                loading={loading}
                sort={sort}
                onSortModelChange={setSort}
                onPageChange={p => setPage(prev => ({...prev, page: p }))}
                onRowsPerPageChange={p => setPage(prev => ({...prev, size: p }))}
                rows={clients}/>
    </ListPageWrapper>
  );
};

export const Clients = withClientListFilterDrawer(withTableSettings(Page, 'clientList'));

const prefillClientOutputInCache = (data: ClientListQuery['clientViews']['data'][0]) => {
  enrollmentApolloClient.writeQuery({
    query : gql`
      query clientViewById($id: String) {
        clientViewById(id: $id) {
          id
          birthDate
          email
          firstName
          lastName
          phoneNumber
          phoneNumber2
          userId
          status
          state
          createdAt
          followUpDate
          expectedCloseDate
          tags {
            expiresAt
            tag {
              color
              id
              name
            }
          }
          affiliationPartnershipSource
          profileSource
          agent {
            firstName
          }
        }
      }
    `,
    data: {
      clientViewById: {
        __typename: 'ClientViewOutput',
        id: data.clientId,
        birthDate: data.birthDate,
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        phoneNumber: data.phoneNumber,
        phoneNumber2: data.phoneNumber2,
        userId: data.userId,
        status: data.status,
        state: data.state,
        createdAt: data.createdAt,
        followUpDate: data.followUpDate,
        expectedCloseDate: data.expectedCloseDate,
        tags: data.tags,
        affiliationPartnershipSource: data.affiliationPartnershipSource,
        profileSource: data.profileSource,
        agent: data.agentName ? {
          firstName: data.agentName
        } : null
      }
    },
    variables: {
      id: data.clientId
    }
  })
}

const filterNameFunc = (name: keyof ClientFilter) => {
  switch (name) {
    case 'followUpDate': return 'Next contact date';
    case 'range': return 'Created';
    case 'aiGeneratedTags': return 'Tag type';
    default: return name;
  }
}

const useFilters = () => {
  return useSessionStorageState<ClientFilter | undefined>(
    'clientListFilters',
    undefined
  );
};

const usePage = () => {
  return useSessionStorageState<PageInput>(
    'clientListPage',
    { page: 0, size: 20 }
  );
};

const useSort = () => {
  return useSessionStorageState<ClientViewSortInput | undefined>(
    'clientListSort',
    {field: Field.CreatedAt, direction: SortDirection.Desc}
  )
}
